// src/v3/ui/backtester/charts/heatmap.tsx

interface HeatmapCell {
  paramX: string;
  paramY: string;
  value: number;
  label?: string;
}

interface HeatmapProps {
  data: HeatmapCell[];
  paramXLabel: string;
  paramYLabel: string;
  valueLabel: string;
}

function colorForValue(value: number, min: number, max: number): string {
  if (max === min) return "hsl(120 50% 50%)";
  const ratio = (value - min) / (max - min);
  const hue = ratio * 120;
  return `hsl(${hue} 70% 45%)`;
}

export function Heatmap({ data, paramXLabel, paramYLabel, valueLabel }: HeatmapProps) {
  if (data.length === 0) {
    return (
      <div className="flex h-64 items-center justify-center text-sm text-muted-foreground">
        No heatmap data
      </div>
    );
  }

  const xValues = [...new Set(data.map((c) => c.paramX))];
  const yValues = [...new Set(data.map((c) => c.paramY))];
  const values = data.map((c) => c.value);
  const min = Math.min(...values);
  const max = Math.max(...values);

  const cellSize = 48;
  const labelOffset = 60;
  const width = xValues.length * cellSize + labelOffset;
  const height = yValues.length * cellSize + labelOffset;

  const getCell = (x: string, y: string) =>
    data.find((c) => c.paramX === x && c.paramY === y);

  return (
    <div className="overflow-auto">
      <svg width={width} height={height} className="max-w-full" data-testid="heatmap-chart">
        <text
          x={labelOffset / 2}
          y={height - 10}
          textAnchor="middle"
          className="fill-muted-foreground"
          style={{ fontSize: 11 }}
        >
          {paramYLabel}
        </text>
        <text
          x={width - 10}
          y={labelOffset / 2}
          textAnchor="end"
          className="fill-muted-foreground"
          style={{ fontSize: 11 }}
        >
          {paramXLabel}
        </text>
        {xValues.map((x, xi) => (
          <text
            key={`x-${x}`}
            x={labelOffset + xi * cellSize + cellSize / 2}
            y={labelOffset - 8}
            textAnchor="middle"
            className="fill-muted-foreground"
            style={{ fontSize: 10 }}
          >
            {x}
          </text>
        ))}
        {yValues.map((y, yi) => (
          <text
            key={`y-${y}`}
            x={labelOffset - 8}
            y={labelOffset + yi * cellSize + cellSize / 2}
            textAnchor="end"
            dominantBaseline="middle"
            className="fill-muted-foreground"
            style={{ fontSize: 10 }}
          >
            {y}
          </text>
        ))}
        {yValues.map((y, yi) =>
          xValues.map((x, xi) => {
            const cell = getCell(x, y);
            if (!cell) return null;
            const color = colorForValue(cell.value, min, max);
            return (
              <g key={`cell-${x}-${y}`}>
                <rect
                  x={labelOffset + xi * cellSize}
                  y={labelOffset + yi * cellSize}
                  width={cellSize - 2}
                  height={cellSize - 2}
                  fill={color}
                  rx={4}
                  data-testid="heatmap-cell"
                />
                <text
                  x={labelOffset + xi * cellSize + cellSize / 2}
                  y={labelOffset + yi * cellSize + cellSize / 2}
                  textAnchor="middle"
                  dominantBaseline="middle"
                  fill="white"
                  style={{ fontSize: 10, fontWeight: 600 }}
                >
                  {cell.value.toFixed(2)}
                </text>
              </g>
            );
          }),
        )}
      </svg>
      <div className="mt-2 flex items-center gap-2 text-xs text-muted-foreground">
        <span>{min.toFixed(2)}</span>
        <div
          className="h-3 w-32 rounded"
          style={{
            background: `linear-gradient(to right, hsl(0 70% 45%), hsl(60 70% 45%), hsl(120 70% 45%))`,
          }}
        />
        <span>{max.toFixed(2)}</span>
        <span className="ml-2">{valueLabel}</span>
      </div>
    </div>
  );
}
